import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Spinner, InputGroup } from "react-bootstrap";
import { useInitiateGlobalPriceChange } from "../../hooks/useInitiateGlobalPriceChange";
import { useMasterPlan } from "../../hooks/useMasterPlan";

const GlobalPriceChangeModal = ({ show, onHide }) => {
  const [basePrice, setBasePrice] = useState("");
  const [pricePerAdditionalUser, setPricePerAdditionalUser] = useState("");
  const [baseplanUsers, setBaseplanUsers] = useState("");
  const [oneTimeSetupPrice, setoneTimeSetupPrice] = useState("");
  const [noticePeriodDays, setNoticePeriodDays] = useState(30);
  const [excludeCustomPlans, setExcludeCustomPlans] = useState(true);
  const [effectiveDate, setEffectiveDate] = useState("");

  const { mutate: initiateChange, isPending } = useInitiateGlobalPriceChange();
  const { data: masterPlan, isLoading: isLoadingMasterPlan } = useMasterPlan(show);

  // Populate form fields with current master plan values when modal opens
  useEffect(() => {
    if (show && masterPlan) {
      setBasePrice(masterPlan.baseplanPrice?.toString() || "");
      setPricePerAdditionalUser(masterPlan.pricePerAdditionalUser?.toString() || "");
      setBaseplanUsers(masterPlan.baseplanUsers?.toString() || "");
      setoneTimeSetupPrice(masterPlan.oneTimeSetipPrice?.toString() || masterPlan.oneTimeSetupPrice?.toString() || "");
    }
  }, [show, masterPlan]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!basePrice || basePrice <= 0 || !noticePeriodDays || noticePeriodDays < 0) {
      return;
    }

    const body = {
      basePrice: parseFloat(basePrice),
      noticePeriodDays: parseInt(noticePeriodDays),
      excludeCustomPlans,
      ...(pricePerAdditionalUser && { pricePerAdditionalUser: parseFloat(pricePerAdditionalUser) }),
      ...(baseplanUsers && { baseplanUsers: parseInt(baseplanUsers) }),
      ...(oneTimeSetupPrice && { oneTimeSetupPrice: parseFloat(oneTimeSetupPrice) }),
      ...(effectiveDate && { effectiveDate }),
    };

    initiateChange(body, {
      onSuccess: () => {
        handleClose();
      },
    });
  };

  const handleClose = () => {
    setBasePrice("");
    setPricePerAdditionalUser("");
    setBaseplanUsers("");
    setoneTimeSetupPrice("");
    setNoticePeriodDays(30);
    setExcludeCustomPlans(true);
    setEffectiveDate("");
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Initiate Global Price Change</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Alert variant="info">
            This will update the master plan pricing immediately. New companies will get the new pricing right away.
            Existing companies will receive a notice period before the price change takes effect.
          </Alert>
          <Form.Group className="mb-3">
            <Form.Label>Base Price *</Form.Label>
            <InputGroup>
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={basePrice}
                onChange={(e) => setBasePrice(e.target.value)}
                required
                placeholder="Enter base monthly price"
              />
            </InputGroup>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Price Per Additional User</Form.Label>
            <InputGroup>
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={pricePerAdditionalUser}
                onChange={(e) => setPricePerAdditionalUser(e.target.value)}
                placeholder="Enter price per additional user"
              />
            </InputGroup>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Base Plan Users</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={baseplanUsers}
              onChange={(e) => setBaseplanUsers(e.target.value)}
              placeholder="Enter number of base plan users"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>One-Time Setup Price</Form.Label>
            <InputGroup>
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={oneTimeSetupPrice}
                onChange={(e) => setoneTimeSetupPrice(e.target.value)}
                placeholder="Enter one-time setup fee"
              />
            </InputGroup>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Notice Period (Days) *</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={noticePeriodDays}
              onChange={(e) => setNoticePeriodDays(e.target.value)}
              required
              placeholder="Enter notice period in days"
            />
            <Form.Text className="text-muted">
              Existing companies will receive this many days notice before the price change takes effect.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Exclude companies with custom plans"
              checked={excludeCustomPlans}
              onChange={(e) => setExcludeCustomPlans(e.target.checked)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Effective Date (Optional)</Form.Label>
            <Form.Control
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
            />
            <Form.Text className="text-muted">
              If not specified, effective date will be calculated as notice date + notice period.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isPending || !basePrice || basePrice <= 0}>
            {isPending ? "Initiating..." : "Initiate Price Change"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default GlobalPriceChangeModal;

